/**
 * Phone Number Input Component
 *
 * Collects the user's phone number.
 * LoadingBar and navigation buttons are rendered by the parent layout.
 */

"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { Input } from "@frontend/shared/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
} from "../insurance-connector-provider";
import {
  type PhoneNumberFormData,
  phoneNumberSchema,
} from "../machine/schemas/validation.schemas";
import { INSURANCE_STEP_FORM_ID } from "../step-config";

export function PhoneNumberInput() {
  const t = useTranslations("InsuranceConnector");
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();

  const form = useForm<PhoneNumberFormData>({
    resolver: zodResolver(phoneNumberSchema),
    defaultValues: {
      phoneNumber: context.phoneNumber || "",
    },
  });

  const onSubmit = (data: PhoneNumberFormData) => {
    send({ type: "SUBMIT", data });
  };

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <h2 className="text-2xl md:text-[32px] font-semibold">
          {t("WhatsYourPhone")}
        </h2>
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="sr-only">{t("PhoneNumber")}</FormLabel>
              <FormControl>
                <Input
                  className="rounded-full py-[31px] text-lg w-full px-5 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0"
                  placeholder={t("EnterPhonePlaceholder")}
                  type="tel"
                  maxLength={10}
                  autoFocus
                  autoComplete="tel"
                  {...field}
                  onChange={(e) => {
                    const digitsOnly = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    field.onChange(digitsOnly);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
